import { Head, router } from '@inertiajs/react'
import { FormEvent, ReactNode, useState } from 'react'
import { HiOutlineTrash } from 'react-icons/hi2'
import AppLayout from '../../components/AppLayout'
import ConfirmDialog from '../../components/ConfirmDialog'
import PageSubHeader from '../../components/PageSubHeader'

// ────────────────────────────────────────────────────────────────
// Types
// ────────────────────────────────────────────────────────────────

type LeadPriority = 'hot' | 'warm' | 'cold'
type LeadStage = 'prospect' | 'in_progress' | 'proposal_sent'
type QuestionType = 'single' | 'multi'
type FieldType = 'text' | 'email' | 'tel' | 'number' | 'url'

type Lead = {
  id: number
  name: string
  email: string
  company: string
  phone: string
  priority: LeadPriority
  stage: LeadStage
  won: boolean
  user_id: number | null
}

type CustomField = {
  key: string
  label: string
  field_type: FieldType
  required: boolean
  value: string
}

type QuestionStep = {
  id: number
  text: string
  description: string
  question_type: QuestionType
  options: Array<{ id: number; label: string }>
  selected_option_ids: number[]
}

type AssignableUser = { id: number; name: string; role: 'manager' | 'sales' }

type Props = {
  event: { id: number; slug: string; title: string }
  lead: Lead
  custom_fields: CustomField[]
  question_steps: QuestionStep[]
  assignable_users: AssignableUser[]
  is_manager: boolean
  errors: Record<string, string[] | undefined>
}

const PRIORITY_LABELS: Record<LeadPriority, string> = {
  hot: 'Hot',
  warm: 'Warm',
  cold: 'Cold',
}

const STAGE_LABELS: Record<LeadStage, string> = {
  prospect: 'Prospect',
  in_progress: 'In Progress',
  proposal_sent: 'Proposal Sent',
}

// ────────────────────────────────────────────────────────────────
// Page
// ────────────────────────────────────────────────────────────────

export default function EditLead({
  lead,
  custom_fields,
  question_steps,
  assignable_users,
  is_manager,
  errors,
}: Props) {
  const [values, setValues] = useState({
    name: lead.name,
    email: lead.email,
    company: lead.company,
    phone: lead.phone,
    priority: lead.priority,
    stage: lead.stage,
    won: lead.won,
    user_id: lead.user_id,
  })

  const [customValues, setCustomValues] = useState<Record<string, string>>(
    Object.fromEntries(custom_fields.map((f) => [f.key, f.value])),
  )

  const [answers, setAnswers] = useState<Record<number, number[]>>(
    Object.fromEntries(question_steps.map((s) => [s.id, s.selected_option_ids])),
  )

  const [processing, setProcessing] = useState(false)
  const [confirmingDelete, setConfirmingDelete] = useState(false)

  const assignee = assignable_users.find((u) => u.id === lead.user_id) || null

  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    setProcessing(true)
    router.patch(
      `/leads/${lead.id}`,
      {
        lead: {
          ...values,
          contact_values: customValues,
          answers_by_step: answers,
        },
      },
      {
        onFinish: () => setProcessing(false),
      },
    )
  }

  const handleDelete = () => {
    router.delete(`/leads/${lead.id}`, {
      onFinish: () => setConfirmingDelete(false),
    })
  }

  return (
    <>
      <Head title={`Edit ${lead.name}`} />
      <AppLayout
        active="dashboard"
        hideTabs
        subHeader={
          <PageSubHeader
            backHref={`/leads/${lead.id}`}
            backLabel="Back to lead"
            title={`Edit ${lead.name}`}
          />
        }
      >
        <form onSubmit={handleSubmit} className="mx-auto w-full max-w-[903px] space-y-6">
          {/* Personal details */}
          <Card title="Personal Details">
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <Field label="Name" error={errors.name} className="md:col-span-2">
                <input
                  type="text"
                  required
                  value={values.name}
                  onChange={(e) => setValues((v) => ({ ...v, name: e.target.value }))}
                  className={inputClass(errors.name)}
                />
              </Field>
              <Field label="Email" error={errors.email}>
                <input
                  type="email"
                  required
                  value={values.email}
                  onChange={(e) => setValues((v) => ({ ...v, email: e.target.value }))}
                  className={inputClass(errors.email)}
                />
              </Field>
              <Field label="Phone" error={errors.phone}>
                <input
                  type="tel"
                  value={values.phone}
                  onChange={(e) => setValues((v) => ({ ...v, phone: e.target.value }))}
                  className={inputClass(errors.phone)}
                />
              </Field>
              <Field label="Company" error={errors.company} className="md:col-span-2">
                <input
                  type="text"
                  required
                  value={values.company}
                  onChange={(e) => setValues((v) => ({ ...v, company: e.target.value }))}
                  className={inputClass(errors.company)}
                />
              </Field>
            </div>
          </Card>

          {/* Workflow */}
          <Card title="Workflow">
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <Field label="Priority">
                <select
                  value={values.priority}
                  onChange={(e) =>
                    setValues((v) => ({ ...v, priority: e.target.value as LeadPriority }))
                  }
                  className={inputClass()}
                >
                  {(['hot', 'warm', 'cold'] as LeadPriority[]).map((p) => (
                    <option key={p} value={p}>
                      {PRIORITY_LABELS[p]}
                    </option>
                  ))}
                </select>
              </Field>

              <Field label="Stage">
                <select
                  value={values.stage}
                  onChange={(e) => setValues((v) => ({ ...v, stage: e.target.value as LeadStage }))}
                  className={inputClass()}
                >
                  {(['prospect', 'in_progress', 'proposal_sent'] as LeadStage[]).map((s) => (
                    <option key={s} value={s}>
                      {STAGE_LABELS[s]}
                    </option>
                  ))}
                </select>
              </Field>

              <Field label="Assignee" className="md:col-span-2">
                {is_manager ? (
                  <select
                    value={values.user_id ?? ''}
                    onChange={(e) =>
                      setValues((v) => ({
                        ...v,
                        user_id: e.target.value === '' ? null : Number(e.target.value),
                      }))
                    }
                    className={inputClass()}
                  >
                    <option value="">Unassigned</option>
                    {assignable_users.map((u) => (
                      <option key={u.id} value={u.id}>
                        {u.name} ({u.role})
                      </option>
                    ))}
                  </select>
                ) : (
                  <p className="rounded-md border border-gray-200 bg-gray-50 px-3 py-4 font-display text-base text-ink">
                    {assignee ? assignee.name : 'Unassigned'}
                  </p>
                )}
              </Field>

              <Field label="Won" className="md:col-span-2">
                <label className="inline-flex cursor-pointer items-center gap-2">
                  <input
                    type="checkbox"
                    checked={values.won}
                    onChange={(e) => setValues((v) => ({ ...v, won: e.target.checked }))}
                    className="h-5 w-5 rounded border-gray-300 text-[#93D500] focus:ring-[#93D500]"
                  />
                  <span className="font-display text-base text-ink">Marked as won</span>
                </label>
              </Field>
            </div>
          </Card>

          {/* Custom contact fields */}
          {custom_fields.length > 0 && (
            <Card title="Contact Details">
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                {custom_fields.map((f) => (
                  <Field
                    key={f.key}
                    label={`${f.label}${f.required ? ' *' : ''}`}
                    error={errors[f.key]}
                    className="md:col-span-2"
                  >
                    <input
                      type={f.field_type}
                      required={f.required}
                      value={customValues[f.key] || ''}
                      onChange={(e) =>
                        setCustomValues((cv) => ({ ...cv, [f.key]: e.target.value }))
                      }
                      className={inputClass(errors[f.key])}
                    />
                  </Field>
                ))}
              </div>
            </Card>
          )}

          {/* Questions */}
          {question_steps.length > 0 && (
            <Card title="Questions">
              <div className="space-y-5">
                {question_steps.map((step) => (
                  <div key={step.id} className="flex flex-col gap-2">
                    <label className="font-display text-base font-medium text-black/60">
                      {step.text}
                    </label>
                    {step.description && (
                      <p className="font-display text-sm text-black/40">{step.description}</p>
                    )}
                    {step.question_type === 'single' ? (
                      <select
                        value={answers[step.id]?.[0] ?? ''}
                        onChange={(e) => {
                          const val = e.target.value
                          setAnswers((a) => ({
                            ...a,
                            [step.id]: val === '' ? [] : [Number(val)],
                          }))
                        }}
                        className={inputClass()}
                      >
                        <option value="">— Select —</option>
                        {step.options.map((o) => (
                          <option key={o.id} value={o.id}>
                            {o.label}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <div className="flex flex-col gap-2">
                        {step.options.map((o) => {
                          const selected = (answers[step.id] || []).includes(o.id)
                          return (
                            <label
                              key={o.id}
                              className="inline-flex cursor-pointer items-center gap-2"
                            >
                              <input
                                type="checkbox"
                                checked={selected}
                                onChange={(e) => {
                                  setAnswers((a) => {
                                    const current = a[step.id] || []
                                    const next = e.target.checked
                                      ? [...current, o.id]
                                      : current.filter((id) => id !== o.id)
                                    return { ...a, [step.id]: next }
                                  })
                                }}
                                className="h-5 w-5 rounded border-gray-300 text-[#93D500] focus:ring-[#93D500]"
                              />
                              <span className="font-display text-base text-ink">{o.label}</span>
                            </label>
                          )
                        })}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </Card>
          )}

          {/* Actions */}
          <div className="flex items-center justify-between gap-2">
            <button
              type="button"
              onClick={() => setConfirmingDelete(true)}
              className="inline-flex items-center gap-2 rounded-md border border-gray-300 bg-white px-5 py-3 font-display text-base font-medium text-ink transition hover:bg-rose-50 hover:text-rose-600"
            >
              <HiOutlineTrash className="h-5 w-5" />
              Delete Lead
            </button>
            <div className="flex items-center gap-2">
              <a
                href={`/leads/${lead.id}`}
                className="rounded-md border border-gray-300 bg-white px-5 py-3 font-display text-base font-medium text-ink transition hover:bg-gray-50"
              >
                Cancel
              </a>
              <button
                type="submit"
                disabled={processing}
                className="rounded-md bg-ink px-7 py-3 font-display text-base font-medium text-white shadow-sm transition hover:bg-midnight-green disabled:cursor-not-allowed disabled:opacity-60"
              >
                {processing ? 'Saving…' : 'Save Changes'}
              </button>
            </div>
          </div>
        </form>
      </AppLayout>

      <ConfirmDialog
        open={confirmingDelete}
        tone="danger"
        title="Delete this lead?"
        body={
          <>
            You're about to delete <strong>{lead.name}</strong> from{' '}
            <strong>{lead.company}</strong>. This cannot be undone.
          </>
        }
        confirmLabel="Delete"
        onConfirm={handleDelete}
        onClose={() => setConfirmingDelete(false)}
      />
    </>
  )
}

// ────────────────────────────────────────────────────────────────
// Helpers
// ────────────────────────────────────────────────────────────────

function Card({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="rounded-xl bg-white shadow-[0_1px_3px_0_rgba(0,0,0,0.1),0_1px_2px_0_rgba(0,0,0,0.06)]">
      <div className="flex items-center gap-4 px-6 pt-5">
        <h2 className="font-display text-lg font-medium text-ink">{title}</h2>
      </div>
      <div className="p-6 pt-4">{children}</div>
    </div>
  )
}

function Field({
  label,
  error,
  children,
  className = '',
}: {
  label: string
  error?: string[]
  children: ReactNode
  className?: string
}) {
  return (
    <label className={`flex flex-col gap-1 ${className}`}>
      <span className="font-display text-base font-medium text-black/60">{label}</span>
      {children}
      {error && error.length > 0 && (
        <span className="mt-1 font-display text-xs text-rose-600">{error.join(', ')}</span>
      )}
    </label>
  )
}

function inputClass(error?: string[]) {
  const base =
    'w-full rounded-md border bg-white px-3 py-4 font-display text-base text-ink shadow-sm placeholder:text-black/40 focus:border-midnight-green focus:outline-none focus:ring-1 focus:ring-midnight-green'
  return `${base} ${error && error.length > 0 ? 'border-rose-400' : 'border-gray-300'}`
}
